import { SubscriptionTier } from "./types";

/**
 * Countries where Homeworks is currently available. New signups are restricted
 * to these — enforced on both the Stripe billing country selector and the
 * company business address. Codes are ISO-3166 alpha-2 (uppercase) to match
 * Stripe's billing details and Google Places `short_name` country codes.
 */
export const SUPPORTED_SIGNUP_COUNTRIES = ["US", "CA", "AU"] as const;

export type SupportedSignupCountry =
  (typeof SUPPORTED_SIGNUP_COUNTRIES)[number];

/** Options for the signup billing country selector. */
export const SUPPORTED_SIGNUP_COUNTRY_OPTIONS: {
  value: SupportedSignupCountry;
  label: string;
  flag: string;
}[] = [
  { value: "US", label: "United States", flag: "🇺🇸" },
  { value: "CA", label: "Canada", flag: "🇨🇦" },
  { value: "AU", label: "Australia", flag: "🇦🇺" },
];

/** Per-country label + placeholder for the billing postal/zip field. */
export const POSTAL_FIELD_BY_COUNTRY: Record<
  SupportedSignupCountry,
  { label: string; placeholder: string }
> = {
  US: { label: "ZIP code", placeholder: "12345" },
  CA: { label: "Postal code", placeholder: "A1A 1A1" },
  AU: { label: "Postcode", placeholder: "2000" },
};

/** Google Places `componentRestrictions.country` expects lowercase alpha-2 codes. */
export const SUPPORTED_SIGNUP_COUNTRY_CODES_LOWER =
  SUPPORTED_SIGNUP_COUNTRIES.map((code) => code.toLowerCase());

/** Notice shown near country inputs. Placement/wording pending design (ENG-3381). */
export const SUPPORTED_COUNTRIES_NOTICE =
  "Homeworks is currently available in the United States, Canada, and Australia.";

export function isSupportedSignupCountry(
  country: string | null | undefined,
): country is SupportedSignupCountry {
  return (
    country != null &&
    (SUPPORTED_SIGNUP_COUNTRIES as readonly string[]).includes(country)
  );
}

export type PricingTier = {
  id: SubscriptionTier;
  name: "Enterprise" | "Growth" | "Solo";
  description: string;
  monthlyText: string;
  monthlyPrice: number;
  annualPrice: number;
  isPopular?: boolean;
  isFree?: boolean;
  features: string[];
  ctaText: string;
};

// TODO JT - switch back to this after testing is complete with legacy tiers
// type PlanById = {
//   [key in Exclude<
//     SubscriptionTier,
//     "PRO" | "STARTER" | "ENTERPRISE_LEGACY"
//   >]: PricingTier;
// };
type TestPlanById = {
  [key in SubscriptionTier]: Omit<PricingTier, "name"> & { name: string };
};

// TODO JT - try to get this from API
export const PLAN_BY_ID: TestPlanById = {
  SOLO: {
    id: "SOLO",
    name: "Solo",
    description:
      "Just getting started? Homeworks gets you on the path to growth, upgrade whenever you're ready!",
    monthlyPrice: 0,
    annualPrice: 0,
    isFree: true,
    monthlyText: "Free forever",
    ctaText: "solo",
    features: [
      "Accept online payments",
      "Create estimates and invoices",
      "Up to 50 customers",
    ],
  },
  GROWTH: {
    id: "GROWTH",
    name: "Growth",
    description:
      "Starting to expand? Homeworks helps you grow to $250,000 with your first employees!",
    monthlyPrice: 299,
    annualPrice: 219,
    monthlyText: "Save $960 with yearly plan",
    ctaText: "growth",
    features: [
      "2-way texting and bulk SMS",
      "Reduced fees with ACH payments",
      "Email marketing & automation",
    ],
  },
  ENTERPRISE: {
    id: "ENTERPRISE",
    name: "Enterprise",
    description:
      "Scaling a 7-figure business? Homeworks enables to you to grow sales, operations, and admin. Without breaking.",
    monthlyPrice: 499,
    annualPrice: 379,
    isPopular: true,
    monthlyText: "Save $1440 with annual plan",
    ctaText: "enterprise",
    features: [
      "Reduced fees for credit/debit card payments",
      "US-based phone and video support",
      "20 users included",
    ],
  },
  // TODO JT - just for testing, remove
  ENTERPRISE_LEGACY: {
    id: "ENTERPRISE_LEGACY",
    name: "Enterprise Legacy",
    description: "Enterprise Legacy",
    monthlyPrice: 100,
    annualPrice: 100,
    ctaText: "enterprise-legacy",
    features: [],
    monthlyText: "",
  },
  PRO: {
    id: "PRO",
    name: "Pro",
    description: "Pro",
    monthlyPrice: 100,
    annualPrice: 100,
    ctaText: "pro",
    features: [],
    monthlyText: "",
  },
  STARTER: {
    id: "STARTER",
    name: "Starter",
    description: "Starter",
    monthlyPrice: 100,
    annualPrice: 100,
    ctaText: "starter",
    features: [],
    monthlyText: "",
  },
};
